<template>
    <div>
        <DsModal
            class="campaign-sms-builder-modal"
            is-open
            show-close
            size="fullscreen"
            :title="$t('funnel.edit.sideBar.flow.automatedSms')"
            @close="handleClose"
        >
            <template #header>
                <div class="header-actions">
                    <DsIconButton
                        data-qa="settings-cog-button"
                        name="settings"
                        :disabled="isDisableHideKmnEnabled || isHideUnverifiedKmnEnabled"
                        @click="isSettingsModalOpen = true"
                    />

                    <DsTextButton
                        data-qa="send-test-button"
                        class="send-test-button"
                        dense
                        :disabled="isSmsSendingBlockedByVerificationStatus || isDisableHideKmnEnabled || isHideUnverifiedKmnEnabled"
                        @click="handleSendTest"
                    >
                        {{ sendTestButton }}
                    </DsTextButton>


                    <DsFilledButton
                        data-qa="done-button"
                        class="done-button"
                        dense
                        :disabled="isSmsSendingBlockedByVerificationStatus || isDisableHideKmnEnabled || isHideUnverifiedKmnEnabled"
                        @click="handleDone"
                    >
                        {{ doneButton }}
                    </DsFilledButton>
                </div>
            </template>

            <template #default>
                <div class="write-stage">
                    <section ref="content" class="content">
                        <div class="content-wrapper">
                            <div
                                v-if="isSmsSendingBlockedByVerificationStatus"
                                class="verification-status"
                                data-qa="ktv-sms-verification-status"
                            >
                                <KtvSmsVerificationStatusAlert
                                    render-if-sending-blocked
                                    show-button
                                    :message="automatedSmsAccountVerification.errorDescription"
                                    :rejection-date="automatedSmsAccountVerification.rejectionDate"
                                    :verification-status="automatedSmsAccountVerification.status"
                                />
                            </div>

                            <ErrorBanner v-if="showError && !isMessageValid" class="error-banner" :message="errorMessage" />

                            <UnverifiedKmnAlert v-if="isHideUnverifiedKmnEnabled" data-qa="unverified-kmn-alert" />

                            <section class="metadata">
                                <div class="from-container">
                                    <DsInputField
                                        class="sms-input"
                                        :label="fromLabel"
                                        readonly
                                        :value="fromNumber"
                                    />

                                    <DsPopover
                                        :is-open="showFromTooltip"
                                        position="right"
                                        position-fixed
                                        @close="showFromTooltip = false"
                                    >
                                        <template #reference>
                                            <DsIconButton
                                                class="tooltip-button"
                                                name="help-circle"
                                                @click="showFromTooltip = !showFromTooltip"
                                                @mouseover="showFromTooltip = true"
                                                @mouseleave="showFromTooltip = false"
                                            />
                                        </template>

                                        <template #default>
                                            <!-- eslint-disable-next-line vue/no-v-html -->
                                            <div class="from-tooltip" v-html="fromToolTip" />
                                        </template>
                                    </DsPopover>
                                </div>
                            </section>

                            <section class="message">
                                <span class="subtitle">{{ textMessage }}</span>
                                <SmsTextBox
                                    v-model="messageContent"
                                    :has-error="showError && !isMessageValid"
                                    :app-id="appId"
                                    :user-id="userId"
                                    :content-editable="!isDisableHideKmnEnabled && !isHideUnverifiedKmnEnabled"
                                    @input="handleUpdate"
                                />

                                <SmsInvalidCharacterAlert v-if="!messageContainsValidCharacters" />
                            </section>

                            <section ref="audience" class="audience">
                                <span class="phone-types-subtitle">{{ phoneNumberText }}</span>

                                <span class="phone-types-description"> {{ selectMultipleText }} </span>

                                <SmsPhoneTypeSelector
                                    v-model="phoneTypes"
                                    class="phone-type-selector"
                                    :show-error="showError && !isPhoneTypesValid"
                                    :read-only="isDisableHideKmnEnabled || isHideUnverifiedKmnEnabled"
                                    @input="updatePhoneTypes"
                                />

                                <DsInlineAlert
                                    v-if="phoneTypes.length > 1"
                                    type="subtle"
                                    leading-icon
                                    class="multiple-phone-types-alert"
                                >
                                    <span>
                                        {{ warningMessage }}

                                        <!--TODO: Convert to ds-link once design system version updated-->
                                        <LinkButton
                                            class="alert-link"
                                            title="See your options"
                                            @click="isPhoneTypesHelpModalOpen = true"
                                        >
                                            {{ warningLink }}
                                        </LinkButton>
                                    </span>
                                </DsInlineAlert>

                                <div ref="consent">
                                    <span :class="['consent-title', { 'consent-error': showConsentError }]"> Do you have permission to text? </span>
                                    <span class="consent-required">*</span>

                                    <DsCheckbox
                                        v-model="complianceCheckboxValue"
                                        class="consent-checkbox"
                                        label="I certify and confirm all contacts added to this automation have provided prior express opt-in consent compliant with all laws to receive marketing texts from my company, and that I will maintain those records as required by applicable laws."
                                        :error="showConsentError"
                                        :disabled="isDisableHideKmnEnabled || isHideUnverifiedKmnEnabled"
                                        @input="updateConsent"
                                    />
                                </div>
                            </section>


                            <div class="sms-broadcast-footer-wrapper">
                                <div class="sms-broadcast-feedback">
                                    <span class="feedback-cta">
                                        {{ missingText }}
                                    </span>

                                    <DsTextButton class="small" data-qa="feedback-button" @click="isFeedbackModalOpen = true">
                                        {{ feedbackLink }}
                                    </DsTextButton>
                                </div>

                                <SmsFooter />
                            </div>
                        </div>
                    </section>

                    <preview
                        class="preview"
                        :account="automatedSmsAccount"
                        :message="messageContent"
                        :show-opt-in-button="!isDisableHideKmnEnabled && !isHideUnverifiedKmnEnabled"
                        @show-settings="isSettingsModalOpen = true"
                    />
                </div>
            </template>
        </DsModal>

        <DsModal
            data-qa="ktv-unable-to-send-sms-modal"
            show-close
            :is-open="isKtvUnableToSendSmsModalOpen"
            :title="$t('sms.verification.unableToSendSmsModal.title')"
            @close="isKtvUnableToSendSmsModalOpen = false"
        >
            <template #default>
                <KtvSmsVerificationStatusAlert
                    no-inline-alert
                    show-button
                    :message="automatedSmsAccountVerification.errorDescription"
                    :rejection-date="automatedSmsAccountVerification.rejectionDate"
                    :verification-status="automatedSmsAccountVerification.status"
                    @action="isKtvUnableToSendSmsModalOpen = false"
                />
            </template>
        </DsModal>

        <SmsAutomationFtuState v-if="!isHideUnverifiedKmnEnabled && accountLoaded && !setupComplete && !smsAccountLoadError && showFtuModal">
            <template #default="ftuProps">
                <FeatureFtuModal
                    is-open
                    :feature-name="ftuProps.feature"
                    :title="ftuProps.title"
                    :description="ftuProps.description"
                    :cta-button-text="ftuProps.cta"
                    :slides="ftuProps.tourSlides"
                    :tour-key="ftuProps.tourKey"
                    illustration="voice-messenger-color"
                    @close="handleClose"
                    @feature-ftu-action="handleTourComplete"
                    @complete="handleTourComplete"
                />
            </template>
        </SmsAutomationFtuState>

        <SmsSetupWorkflow
            v-if="showSmsSetupWorkflow"
            :from-number="fromNumber"
            :is-kmn-branded-opt-in-enabled="isKmnBrandedOptInEnabled"
            :kmn-phone-verification-enabled="kmnPhoneVerificationEnabled"
            :is-billing-changes-enabled="isBillingChangesEnabled"
            @account-created="handleAccountCreated"
            @close="handleClose"
            @setup-complete="handleSetupComplete"
        />

        <KmnVerificationForm
            v-if="showKmnVerificationModal"
            :is-open="isKmnVerificationModalOpen"
            :kmn-account="automatedSmsAccount"
            :is-kmn-branded-opt-in-enabled="isKmnBrandedOptInEnabled"
            @complete="handleCompleteKmnVerificationModal"
            @close="handleCloseKmnVerificationModal"
        />

        <SmsSendTestModal
            :from="fromNumber"
            :is-open="isSendTestModalOpen"
            :message="messageContent"
            @close="isSendTestModalOpen = false"
        />

        <SmsMultiplePhoneAlertModal
            :is-open="isPhoneTypesHelpModalOpen"
            @close="isPhoneTypesHelpModalOpen = false"
        />

        <TextMarketingSettingsModal
            v-if="isSettingsModalOpen"
            :is-billing-changes-enabled="isBillingChangesEnabled"
            :account="automatedSmsAccount || {}"
            @close="isSettingsModalOpen = false"
            @update="handleAccountUpdate"
        />

        <SmsErrorModal
            v-if="isErrorModalOpen"
            @close="handleErrorModalClose"
        />

        <SmsFeedbackModal v-if="isFeedbackModalOpen" source="Automations" @close="isFeedbackModalOpen = false" />

        <portal-target name="kmnTemplateManager" />

        <DsFocusOnKeyboardOnly />
    </div>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash.debounce';
import ErrorBanner from '@/components/shared/ErrorBanner';
import Preview from './SmsPreview';
import SmsFooter from './SmsFooter';
import SmsFeedbackModal from '@/components/shared/SmsFeedbackModal';
import SmsPhoneTypeSelector from './SmsPhoneTypeSelector';
import SmsSetupWorkflow from './setup/SmsSetupWorkflow';
import SmsSendTestModal from './SmsSendTestModal';
import TextMarketingSettingsModal from './TextMarketingSettingsModal';
import SmsErrorModal from './SmsErrorModal';
import SmsAutomationFtuState from './SmsAutomationFtuState';
import FeatureFtuModal from '../onboarding/components/FeatureFtuModal.vue';
import LinkButton from '@/components/shared/LinkButton';
import SmsInvalidCharacterAlert from './SmsInvalidCharacterAlert';
import SmsTextBox from './SmsTextBox';
import KmnVerificationForm from '@/components/broadcast/sms/KmnVerificationForm';
import KtvSmsVerificationStatusAlert from '@/components/automatedSms/KtvSmsVerificationStatusAlert';
import UnverifiedKmnAlert from '../shared/UnverifiedKmnAlert.vue';
import {
    KMN_VERIFICATION_STATUS_TYPE,
    POST_20240130_SENDABLE_TOLL_FREE_SMS_STATUSES,
    SMS_MESSAGE_LENGTH,
} from '@/components/broadcast/constants/smsBroadcast.constants';
import { getSmsAccountVerification } from '@/api/broadcasts/smsBroadcasts';
import { loadFtu } from '@/ftu.utils';
import { formatPhone } from './phone.util';
import { containsSupportedCharacters } from './automatedSms.util';

import { isSpaApp } from '@/api/shared';

import {
    trackSmsAccountProvisioningStart,
    trackSmsAutomationCreated,
} from './smsAnalytics';
import SmsMultiplePhoneAlertModal from './SmsMultiplePhoneAlertModal';
import moment from 'moment-timezone';

const VERIFICATION_DISPLAY_TYPES = [
    KMN_VERIFICATION_STATUS_TYPE.EXPIRED,
    KMN_VERIFICATION_STATUS_TYPE.RESUBMISSION,
    KMN_VERIFICATION_STATUS_TYPE.NOT_VERIFIED,
];

const PERMANENT_VERIFICATION_TYPES = [
    KMN_VERIFICATION_STATUS_TYPE.APPROVED,
    KMN_VERIFICATION_STATUS_TYPE.REJECTED,
];

export default {
    components: {
        SmsMultiplePhoneAlertModal,
        Preview,
        SmsFooter,
        SmsSetupWorkflow,
        SmsSendTestModal,
        TextMarketingSettingsModal,
        SmsFeedbackModal,
        SmsPhoneTypeSelector,
        SmsErrorModal,
        SmsAutomationFtuState,
        FeatureFtuModal,
        LinkButton,
        SmsInvalidCharacterAlert,
        SmsTextBox,
        ErrorBanner,
        KmnVerificationForm,
        KtvSmsVerificationStatusAlert,
        UnverifiedKmnAlert,
    },

    props: {
        id: {
            type: Number,
            default: 0,
        },

        funnelId: {
            type: String,
            default: '',
        },

        userId: String,

        appId: String,

        debounceDelay: {
            type: Number,
            default: 500,
        },
        eventSource: {
            type: String,
            default: 'Automations',
        },

        kmnPhoneVerificationEnabled: Boolean,

        isKmnBrandedOptInEnabled: Boolean,

        isHideUnverifiedKmnEnabled: Boolean,

        isBillingChangesEnabled: Boolean,

        isDisableHideKmnEnabled: Boolean,
    },

    data() {
        return {
            automatedSmsAccount: null,
            automatedSmsAccountVerification: {},
            accountLoaded: false, // resolves on smsAccount load attempt (successful or not)
            setupComplete: false,
            isOpen: false,
            loading: false,
            marketingAutomatedSmsId: null,
            messageContent: '',
            phoneTypes: [],
            consentConfirmationTime: null,
            consentAcceptedBy: null,
            showFromTooltip: false,
            isSendTestModalOpen: false,
            isSettingsModalOpen: false,
            isFeedbackModalOpen: false,
            isErrorModalOpen: false,
            isPhoneTypesHelpModalOpen: false,
            smsAccountLoadError: false,
            showError: false,
            showFtuModal: true,
            showSmsSetupWorkflow: false,
            ftu: null,
            loadingFtu: true,
            isSpaApp: false,
            textMessage: '',
            phoneNumberText: '',
            selectMultipleText: '',
            fromLabel: '',
            fromToolTip: '',
            sendTestButton: '',
            doneButton: '',
            warningMessage: '',
            warningLink: '',
            missingText: '',
            feedbackLink: '',
            warningText: '',
            isKmnVerificationModalOpen: false,
            isKtvUnableToSendSmsModalOpen: false,
        };
    },

    created() {
        if (this.debounceDelay > 0) {
            this.updateAutomationMessage = debounce(this.updateAutomationMessage, this.debounceDelay);
        }

        if (window.keapI18nMap !== undefined) {
            this.textMessage = window.keapI18nMap['funnel.campaign.communications.textmsg.header.label'];
            this.phoneNumberText = window.keapI18nMap['funnel.campaign.communications.textmsg.phonenumber.text'];
            this.selectMultipleText = window.keapI18nMap['funnel.campaign.communications.textmsg.selectmultiple.text'];
            this.fromLabel = window.keapI18nMap['funnel.campaign.communications.textmsg.from.label'];
            this.fromToolTip = window.keapI18nMap['funnel.campaign.communications.textmsg.from.tolltip'];
            this.sendTestButton = window.keapI18nMap['funnel.campaign.communications.textmsg.sendtest.button'];
            this.doneButton = window.keapI18nMap['funnel.campaign.communications.textmsg.done.button'];
            this.warningMessage = window.keapI18nMap['funnel.campaign.communications.textmsg.warningmsg'];
            this.warningLink = window.keapI18nMap['funnel.campaign.communications.textmsg.warningmsg.link'];
            this.missingText = window.keapI18nMap['funnel.campaign.communications.textmsg.missingtext'];
            this.feedbackLink = window.keapI18nMap['funnel.campaign.communications.textmsg.feedbacklink'];
            this.warningText = window.keapI18nMap['funnel.campaign.communications.textmsg.warning.text'];
        } else {
            this.textMessage = 'Write your message';
            this.phoneNumberText = 'Phone number delivery options';
            this.selectMultipleText = 'Which phone number(s) do you want to send to? (You can select multiple)';
            this.fromLabel = 'From';
            this.fromToolTip = 'This is your <b>Keap Marketing Number</b>. Recipients will see it each time you send a text message broadcast or automated text message.';
            this.sendTestButton = 'Send test';
            this.doneButton = 'Done';
            this.warningMessage = 'Because you have multiple phone types added, some recipients may get this message more than once.';
            this.warningLink = 'See your options';
            this.missingText = 'Missing something? Let us know.';
            this.feedbackLink = 'Give feedback';
            this.warningText = 'A message is required.';
        }
    },

    mounted() {
        const [modal] = document.getElementsByClassName('campaign-sms-builder-modal');

        modal?.addEventListener('scroll', this.scrollToModalTop);

        this.onMounted();

        this.checkApp();
    },

    beforeDestroy() {
        const [modal] = document.getElementsByClassName('campaign-sms-builder-modal');

        modal?.removeEventListener('scroll', this.scrollToModalTop);
    },

    watch: {
        showKmnVerificationModal: {
            handler(val) {
                this.isKmnVerificationModalOpen = val;
            },
        },
    },

    computed: {
        complianceCheckboxValue: {
            set(value) {
                if (value) {
                    this.consentConfirmationTime = `${moment().unix()}`;
                } else {
                    this.consentConfirmationTime = null;
                }
            },
            get() {
                return Boolean(this.consentConfirmationTime);
            },
        },

        showConsentError() {
            return this.showError && !this.isConsentAccepted;
        },

        errorMessage() {
            const errors = [];
            let message = this.warningText;

            if (this.isMessageTooLong) {
                errors.push(this.$tc('sms.broadcast.campaignBuilder.error.pleaseShortenMessage', this.maxCharacterCount, { count: this.maxCharacterCount }));
            }

            if (!this.messageContainsValidCharacters) {
                errors.push(this.$t('sms.broadcast.richTextBox.error.unsupportedCharacters'));
            }

            if (errors.length > 1) {
                message = this.$t('sms.broadcast.richTextBox.error.multipleErrors');
            } else if (errors.length === 1) {
                [message] = errors;
            }

            return message;
        },

        fromNumber() {
            return this.automatedSmsAccount != null
                ? formatPhone(this.automatedSmsAccount.phoneNumber)
                : '';
        },

        isMessageTooLong() {
            return this.messageContent?.length > this.maxCharacterCount;
        },

        isMessageValid() {
            return this.messageContent?.length > 0 && !this.isMessageTooLong && this.messageContainsValidCharacters;
        },

        messageContainsValidCharacters() {
            return containsSupportedCharacters(this.messageContent);
        },

        isPhoneTypesValid() {
            return this.phoneTypes.length > 0;
        },

        isConsentAccepted() {
            return Boolean(this.consentConfirmationTime);
        },

        showVerificationForm() {
            return !this.isHideUnverifiedKmnEnabled
                && (!this.automatedSmsAccount?.verificationStatus || VERIFICATION_DISPLAY_TYPES.includes(this.automatedSmsAccount.verificationStatus));
        },

        showKmnVerificationModal() {
            return this.kmnPhoneVerificationEnabled && !this.showSmsSetupWorkflow && this.automatedSmsAccount?.phoneNumber && this.showVerificationForm;
        },

        maxCharacterCount() {
            return SMS_MESSAGE_LENGTH.MAXIMUM;
        },

        isSmsSendingBlockedByVerificationStatus() {
            return this.automatedSmsAccountVerification.status
                && !POST_20240130_SENDABLE_TOLL_FREE_SMS_STATUSES
                    .includes(this.automatedSmsAccountVerification.status);
        },
    },

    methods: {
        async checkApp() {
            this.isSpaApp = await isSpaApp();
        },

        async createAutomationMessage() {
            this.loading = true;

            try {
                const { data } = await axios.post('/app/automatedSmsFunnel', {
                    funnelId: this.funnelId,
                    phoneTypes: ['Mobile', 'Work', 'Home', 'Other'],
                });

                this.marketingAutomatedSmsId = data.marketingAutomatedSmsId;
                this.automationMessageId = data.automationMessageId;
                this.phoneTypes = ['Mobile', 'Work', 'Home', 'Other'];
                trackSmsAutomationCreated(this.eventSource, this.isSpaApp);
                window.parent.dispatchEvent(new CustomEvent('automationMessageCreated', {
                    detail: {
                        marketingAutomatedSmsId: this.marketingAutomatedSmsId,
                        automationMessageId: data.automationMessageId,
                    },
                }));
            } catch (e) {
                this.$toast({ message: 'There was an error loading your message. Please try again.' });
            }

            this.loading = false;
        },

        handleClose() {
            if (!this.isMessageValid || !this.isPhoneTypesValid || !this.isConsentAccepted) {
                window.parent.dispatchEvent(new Event('automationMessageNotReady'));
            }
            window.parent.dispatchEvent(new Event('closeCampaignSmsBuilderModal'));
        },

        handleDone() {
            if (this.isSmsSendingBlockedByVerificationStatus) {
                this.isKtvUnableToSendSmsModalOpen = true;

                return;
            }

            if (this.isMessageValid && this.isPhoneTypesValid && this.isConsentAccepted) {
                window.parent.dispatchEvent(new Event('automationMessageReady'));
                this.handleClose();
            } else {
                this.showError = true;

                try {
                    if (!this.isMessageValid) {
                        this.$refs.content?.scrollTo(0, 0);
                    } else if (!this.isPhoneTypesValid) {
                        this.$refs.content?.scrollTo(0, this.$refs.audience.offsetTop);
                    } else if (!this.isConsentAccepted) {
                        this.$refs.content?.scrollTo(0, this.$refs.consent.offsetTop);
                    }
                } catch (e) { /* do nothing */ }
            }
        },

        handleSendTest() {
            if (this.isSmsSendingBlockedByVerificationStatus) {
                this.isKtvUnableToSendSmsModalOpen = true;

                return;
            }

            if (this.isMessageValid) {
                this.isSendTestModalOpen = true;
            } else {
                this.showError = true;
            }
        },

        handleAccountCreated(account) {
            this.automatedSmsAccount = account;
            this.createAutomationMessage();
        },

        handleAccountUpdate(updatedAccount) {
            this.automatedSmsAccount = {
                ...this.automatedSmsAccount,
                ...updatedAccount,
            };
        },

        handleSetupComplete() {
            this.setupComplete = true;
        },

        async loadAutomatedSmsAccount() {
            this.loading = true;

            try {
                const { data } = await axios.get('/app/automatedSmsFunnel/getAccount');

                if (data) {
                    this.setupComplete = true;
                }

                this.automatedSmsAccount = data;
            } catch (e) {
                this.smsAccountLoadError = true;
                this.isErrorModalOpen = true;
            }

            this.loading = false;
        },

        async loadAutomationMessage(id) {
            this.loading = true;

            try {
                const { data } = await axios.get(`/app/automatedSmsFunnel/${id}`);

                this.marketingAutomatedSmsId = id;
                this.messageContent = data.message || '';
                this.phoneTypes = data.phoneTypes || ['Mobile', 'Work', 'Home', 'Other'];
                this.consentConfirmationTime = data.consentConfirmationTime || null;
                this.consentAcceptedBy = data.consentAcceptedBy || null;
            } catch (e) {
                this.$toast({ message: 'There was an error loading your message. Please try again.' });
            }

            this.loading = false;
        },

        async loadFtu() {
            this.loadingFtu = true;
            this.ftu = await loadFtu(this.appId, this.userId);
            this.loadingFtu = false;
        },

        async onMounted() {
            this.loadFtu();
            await this.loadAutomatedSmsAccount();

            this.accountLoaded = true;

            if (!this.automatedSmsAccount) {
                return;
            }

            if (this.id) {
                await this.loadAutomationMessage(this.id);
            } else {
                await this.createAutomationMessage();
            }

            await this.loadSmsAccountVerification();
        },

        async loadSmsAccountVerification() {
            try {
                if (!this.automatedSmsAccount) {
                    return;
                }

                if (PERMANENT_VERIFICATION_TYPES.includes(this.automatedSmsAccountVerification.status)) {
                    return;
                }

                if (this.automatedSmsAccount.phoneNumber) {
                    const { automatedSmsAccount: { phoneNumber } } = this;

                    this.automatedSmsAccountVerification = await getSmsAccountVerification(phoneNumber);
                }
            } catch { /* do nothing */ }
        },

        handleUpdate(message) {
            this.consentConfirmationTime = null;

            this.messageContent = message;

            if (this.isMessageValid && this.showError) {
                this.showError = false;
            }

            if (this.messageContainsValidCharacters) {
                this.updateAutomationMessage({ message, phoneTypes: this.phoneTypes, consentConfirmationTime: this.consentConfirmationTime });
            }
        },

        async updateAutomationMessage({ message, phoneTypes, consentConfirmationTime }) {
            this.loading = true;

            const consentAcceptedBy = consentConfirmationTime ? this.userId : null;

            try {
                await axios.put(`/app/automatedSmsFunnel/${this.marketingAutomatedSmsId}`, {
                    message, phoneTypes, consentConfirmationTime, consentAcceptedBy,
                });
                window.parent.dispatchEvent(new Event('automationMessageChanged'));
            } catch (e) {
                this.$toast({ message: 'There was an error updating your message. Please try again.' });
            }

            this.loading = false;
        },

        updatePhoneTypes(phoneTypes) {
            this.consentConfirmationTime = null;

            this.phoneTypes = phoneTypes;
            this.updateAutomationMessage({ message: this.messageContent, phoneTypes, consentConfirmationTime: this.consentConfirmationTime });

            if (this.isPhoneTypesValid && this.showError) {
                this.showError = false;
            }
        },

        updateConsent() {
            this.updateAutomationMessage({ message: this.messageContent, phoneTypes: this.phoneTypes, consentConfirmationTime: this.consentConfirmationTime });
        },

        handleErrorModalClose() {
            this.isErrorModalOpen = false;
            this.handleClose();
        },

        handleTourComplete() {
            trackSmsAccountProvisioningStart(this.eventSource, this.isSpaApp);
            this.showFtuModal = false;
            this.showSmsSetupWorkflow = true;
        },

        handleCloseKmnVerificationModal() {
            this.loadSmsAccountVerification();
            this.handleClose();
        },

        handleCompleteKmnVerificationModal() {
            this.isKmnVerificationModalOpen = false;

            this.automatedSmsAccount = {
                ...this.automatedSmsAccount,
                verificationStatus: KMN_VERIFICATION_STATUS_TYPE.PENDING_VERIFICATION,
            };

            this.loadSmsAccountVerification();
        },

        scrollToModalTop() {
            const [modal] = document.getElementsByClassName('campaign-sms-builder-modal');

            modal?.scrollTo(0, 0);
        },
    },
};
</script>

<style lang="scss" scoped>
    .campaign-sms-builder-modal {
        --modal-padding: 0;
    }

    .header-actions {
        display: flex;
        gap: $spacing-100;
        align-items: center;
        padding: 0 $spacing-250;
    }

    .done-options {
        --button-padding-left: #{$spacing-100};
        --button-padding-right: #{$spacing-100};
    }

    .write-stage {
        display: flex;
        height: calc(100vh - #{px-to-rem(56px)});
    }

    .content {
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 3;
        scroll-behavior: smooth;

        &-wrapper {
            padding-top: $spacing-400;
            height: 100%;
            width: 70%;
            display: flex;
            flex-direction: column;

            @media($small) {
                width: 100%;
                padding: $spacing-200 $spacing-300 0;
            }
        }
    }

    .error-banner {
        margin-bottom: $spacing-400;
    }

    section.metadata,
    section.audience,
    section.message {
        display: flex;
        flex-direction: column;
        margin-bottom: $spacing-200;
    }

    .title {
        font-family: $font-family-secondary-title;
        font-size: $font-size-xl;
        font-weight: $font-weight-bold;
        line-height: $line-height-xl;
        margin-bottom: $spacing-200;
    }

    .subtitle {
        font-size: $font-size-md;
        line-height: $line-height-lg;
        font-weight: $font-weight-semibold;
        margin-bottom: $spacing-100;
    }

    .phone-types-description {
        font-size: $font-size-sm;
        margin-bottom: $spacing-200;
    }

    .phone-types-subtitle {
        font-size: $font-size-md;
        line-height: $line-height-lg;
        font-weight: $font-weight-semibold;
        margin-bottom: $spacing-050;
    }

    .from-container {
        display: flex;
    }

    .tooltip-button {
        margin: $spacing-050 $spacing-100 0;

        --icon-color: #{$color-gray-darker};
        --icon-size: 1rem;
    }

    .from-tooltip {
        padding: $spacing-200;
        font-size: $font-size-md;
        line-height: $line-height-lg;
        .text-bold {
            font-weight: $font-weight-bold;
        }
    }

    .sms-input {
        max-width: px-to-rem(328px);
        width: 100%;

        @media($small) {
            max-width: initial;
        }
    }

    .sms-broadcast-footer-wrapper {
        margin-top: auto;
    }

    .multiple-phone-types-alert {
        margin-bottom: $spacing-400;
        font-size: $font-size-sm;
    }

    .sms-broadcast-feedback {
        display: flex;
        align-items: center;
        font-size: $font-size-xs;
        gap: $spacing-200;

        .feedback-cta {
            margin-bottom: 0;
            color: $color-gray-700;
        }
    }

    .preview {
        flex: 2;
    }

    .alert-link {
        color: $color-text-normal;
        font-size: $font-size-md;
        text-decoration: underline;
        line-height: $line-height-lg;
        vertical-align: initial;
        font-weight: $font-weight-regular;
    }

    .consent-title {
        font-weight: $font-weight-bold;
        font-size: $font-size-md;

        &.consent-error {
            color: $color-red-800;
        }
    }

    .consent-required {
        color: $color-red-800;
    }

    .consent-checkbox {
        --checkbox-align-items: flex-start;
        font-size: $font-size-sm;
        line-height: $line-height-md;
    }

    .verification-status:not(:empty) {
        margin-bottom: $spacing-300;
        width: 100%;
    }
</style>
